import { useQuery } from '@tanstack/react-query';
import { useStoreState } from '~/store';
import { simulationService } from '~/services';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { DEFAULT_SIMULATION_SERVICE_VERSION, type SimulationServiceVersion } from '~/store/simulation/types';

/**
 * Resolves the engine version for a simulation.
 *  1. In-memory wizard stamp (simulationSetupData.serviceVersion) wins — no network.
 *  2. Else, if a simulationId exists, probe the v2 service (404 => 'v1').
 *  3. Else default to 'v2'.
 */
export function useSimulationVersion(simulationId?: string | null): {
  version: SimulationServiceVersion;
  isResolving: boolean;
} {
  const stamped = useStoreState((state) => state.simulation.simulationSetupData.serviceVersion);

  const shouldProbe = !stamped && !!simulationId;

  const probe = useQuery({
    ...useQueryCreator(simulationService.simulationV2.versionProbe, { args: [simulationId ?? ''] }),
    enabled: shouldProbe,
  });

  if (stamped) {
    return { version: stamped, isResolving: false };
  }
  if (shouldProbe) {
    return {
      version: (probe.data as SimulationServiceVersion | undefined) ?? DEFAULT_SIMULATION_SERVICE_VERSION,
      isResolving: probe.isLoading,
    };
  }
  return { version: DEFAULT_SIMULATION_SERVICE_VERSION, isResolving: false };
}
